import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LocalDate } from "@js-joda/core";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import type { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import {
  getApplicationFilterOptions,
  isApplicationFilter,
} from "./applicationList.filter.logic";
import type {
  ApplicationFilter,
  ApplicationListView,
} from "./applicationList.filter.logic";
import {
  ApplicationFilterControls,
  PendingCountBadge,
  SegmentOptionContent,
} from "./applicationList.styled";

interface Props {
  readonly applicationsCount: number;
  readonly filter: ApplicationFilter;
  readonly selectedDate: LocalDate | null;
  readonly status: ApplicationListView;
  readonly onDateChange: (date: LocalDate | null) => void;
  readonly onFilterChange: (filter: ApplicationFilter) => void;
  readonly onStatusChange: (status: ApplicationListView) => void;
}

export const ApplicationListFilters = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const segmentOptions = useMemo<SegmentOption<ApplicationListView>[]>(
    () => [
      {
        value: "Pending",
        label: (
          <SegmentOptionContent>
            {t("ApplicationsCompany.NewApplications")}
            {props.applicationsCount > 0 && (
              <PendingCountBadge>{props.applicationsCount}</PendingCountBadge>
            )}
          </SegmentOptionContent>
        ),
      },
      { value: "All", label: t("GigsCompany.All") },
    ],
    [props.applicationsCount, t],
  );
  const filterOptions = useMemo(() => getApplicationFilterOptions(t), [t]);

  return (
    <ApplicationFilterControls>
      <SegmentController
        options={segmentOptions}
        value={props.status}
        onChange={props.onStatusChange}
      />
      <Dropdown
        ariaLabel={t("ApplicationsCompany.Status")}
        disabled={props.status === "Pending"}
        label={t("ApplicationsCompany.Status")}
        options={filterOptions}
        value={props.filter}
        onChange={(value) => {
          if (isApplicationFilter(value)) {
            props.onFilterChange(value);
          }
        }}
      />
      <DatePicker
        label={t("ApplicationsCompany.Date")}
        allowClear
        value={props.selectedDate}
        onChange={props.onDateChange}
      />
    </ApplicationFilterControls>
  );
};
